import React, { useEffect, useMemo, useState } from 'react';

import { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { NewMessageRecipientSelector } from './new-message-recipient-selector';
import { sendBulkMessage } from '../../contacts/actions/send-bulk-message';
import { sendText } from '../../contacts/actions/send-text';
import { loadEmployeeGroups } from '../../employee-groups/actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../../employee-groups/employee-group-selectors';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { userSelector } from '../../reducers/current-user-selectors';
import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';

import { stateCityOptionalStepProvider } from '../../employees/city-state-provider';
import { formatEmployeeGroupName } from '../employee-message-utils';

import { NewAnnouncementComposer } from './new-announcement-composer';
import { NewEmployeeMessageComposer } from './new-employee-message-composer';

import { employeeName } from '../../employees/utils/employee-name';
import {
  getEmployeeGroupCountsFromEmployees,
  getEmployeeGroupsFromEmployees,
} from '../../employees/utils/get-employee-groups-from-employees';
import { allEmployeesKey, allEmployeesLabel } from '../../util/all-employees-option';
import { EmployeeCanMessage } from '../../util/can-message';
import { MESSAGE_TYPE } from '../../util/message-types';
import { LoadingMessage } from '../../widgets/loading-message';

const mapStateToProps = (state: any, { orgId }: InputProps) => {
  const currentEmployees = (employeesForOrgIdSelector(state, orgId) || []).filter(
    (emp) => emp.isCurrentEmployee,
  );
  const allEmployeeGroups = employeeGroupsForOrgIdSelector(state, orgId);

  return {
    employees: currentEmployees,
    employeeGroups: getEmployeeGroupsFromEmployees(currentEmployees, allEmployeeGroups),
    user: userSelector(state),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    sendMessage: (
      contact: { contactId: number; context: ContactContextType },
      messageBody: string,
      messageType: string,
      subject?: string | null,
      sendDateTime?: Moment | null,
      urlMappings?: Record<string, string>,
      sendVCardForOrgId?: number,
    ) =>
      dispatch(
        sendText(
          contact.contactId,
          contact.context,
          messageBody,
          messageType,
          subject,
          { orgId },
          sendDateTime,
          urlMappings,
          sendVCardForOrgId,
        ),
      ),
    sendBulkMessage: (
      contacts: Array<{ contactId: number; context: ContactContextType }>,
      messageBody: string,
      messageType: string,
      hasVCardAttached: boolean,
      subject?: string | null,
      sendDateTime?: Moment | null,
      urlMappings?: Record<string, string>,
    ) =>
      dispatch(
        sendBulkMessage(
          contacts,
          messageBody,
          messageType,
          hasVCardAttached,
          subject,
          { orgId },
          sendDateTime,
          urlMappings,
        ),
      ),
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(orgId)),
    loadOrgCurrentEmployees: () =>
      dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
  },
});

type InputProps = {
  orgId: number;
  onMessageSent?: (employeeId: number) => void;
  onAnnouncementSent?: () => void;
  onClose: () => void;
  hideEmployeeOptions?: boolean;
  hideGroupOptions?: boolean;
  initialMessage?: string;
  // Announcement Only options:
  hideThread?: boolean;
  defaultToAllEmployees?: boolean;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export type RecipientType = 'employee' | 'group';
export const RECIPIENT_TYPE: Record<string, RecipientType> = {
  EMPLOYEE: 'employee',
  GROUP: 'group',
} as const;

const NewMessageComposerComponent = ({
  actions,
  user,
  employees,
  employeeGroups,
  onMessageSent,
  onAnnouncementSent,
  orgId,
  onClose,
  initialMessage,
  hideEmployeeOptions,
  hideGroupOptions,
  hideThread,
  defaultToAllEmployees,
}: Props) => {
  const allEmployeesOption = useMemo(
    () => ({
      label: `${allEmployeesLabel(user)} (${employees.length || 0})`,
      value: allEmployeesKey(orgId),
    }),
    [employees.length, orgId, user],
  );

  const [recipientType, setRecipientType] = useState<RecipientType>(
    hideEmployeeOptions ? RECIPIENT_TYPE.GROUP : RECIPIENT_TYPE.EMPLOYEE,
  );
  const [selectedEmployees, setSelectedEmployees] = useState<Array<SelectOption<string>>>([]);
  const [selectedGroup, setSelectedGroup] = useState<SelectOption<string> | null>(null);

  useEffect(() => {
    // To properly update the employee count in the label, we set the all employee default in a useEffect
    if (defaultToAllEmployees) {
      setSelectedGroup(allEmployeesOption);
    }
  }, [allEmployeesOption, defaultToAllEmployees]);

  useEffect(() => {
    actions.loadOrgCurrentEmployees();
    actions.loadOrgEmployeeGroups();
  });

  if (!employeeGroups || !employees) {
    return <LoadingMessage />;
  }

  const getEmployeeOptions = () => {
    const arr = employees || [];
    return arr
      .filter((e: Employee) => {
        const canMessage = new EmployeeCanMessage(e);
        return (
          canMessage.canReceive(MESSAGE_TYPE.TEXT) || canMessage.canReceive(MESSAGE_TYPE.EMAIL)
        );
      })
      .map((e: Employee) => ({
        label: employeeName(e),
        value: e.employeeId.toString(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const getGroupOptions = () => {
    const employeeGroupMemberCounts = getEmployeeGroupCountsFromEmployees(employees);
    const groupOptions = employeeGroups
      .map((eg) => ({
        label: formatEmployeeGroupName(eg.name, employeeGroupMemberCounts[eg.employeeGroupId]),
        value: eg.employeeGroupId.toString(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [allEmployeesOption, ...groupOptions];
  };

  const getSelectedEmployees = (): Array<Employee> => {
    const selectedEmployeeIds = selectedEmployees.map((se) => se.value);
    return employees.filter((e) => selectedEmployeeIds.includes(e.employeeId.toString()));
  };

  const getSelectedEmployeeGroup = () => {
    if (!selectedGroup) {
      return null;
    }
    if (selectedGroup.value === allEmployeesKey(orgId)) {
      return selectedGroup.value;
    }

    const parsedGroupId = parseInt(selectedGroup?.value, 10);
    return Number.isNaN(parsedGroupId) ? null : parsedGroupId;
  };

  const handleEmployeeSelection = (employeeArray: Array<{ value: string; label: string }>) => {
    setSelectedEmployees(employeeArray || []);
  };

  const handleGroupSelection = (groupOption: SelectOption<string> | null) => {
    setSelectedGroup(groupOption);
  };

  const handleRecipientTypeChange = (type: RecipientType) => {
    setSelectedEmployees([]);
    setSelectedGroup(null);
    setRecipientType(type);
  };

  const isSelectingEmployees = recipientType === RECIPIENT_TYPE.EMPLOYEE;
  const placeholderText = isSelectingEmployees
    ? 'Select employees to send a message to'
    : 'Select a group to send an announcement';
  const autofocus = isSelectingEmployees ? selectedEmployees.length === 0 : !selectedGroup;

  return (
    <div className="u-display-flex u-flexDirection-column u-height-100 u-overflow-auto">
      <div className="u-border-bottom u-height-50px">
        <div className="u-maxWidth-800px u-flex-center u-justifyContent-center u-margin-0Auto u-padding-x">
          <div className="u-margin-right typo-h3-v2">To:</div>
          <NewMessageRecipientSelector
            recipientType={recipientType}
            recipientTypeHandler={handleRecipientTypeChange}
            value={isSelectingEmployees ? selectedEmployees : selectedGroup}
            onChange={isSelectingEmployees ? handleEmployeeSelection : handleGroupSelection}
            options={isSelectingEmployees ? getEmployeeOptions() : getGroupOptions()}
            placeholder={placeholderText}
            autofocus={autofocus}
            isMulti={isSelectingEmployees}
            isClearable={!hideEmployeeOptions}
            hideRecipientToggle={hideEmployeeOptions || hideGroupOptions}
          />
        </div>
      </div>
      <div className="u-height-100 u-overflow-auto u-padding-xl">
        {recipientType === RECIPIENT_TYPE.EMPLOYEE ? (
          <NewEmployeeMessageComposer
            orgId={orgId}
            selectedEmployees={getSelectedEmployees()}
            onClose={onClose}
            onMessageSent={onMessageSent}
            initialMessage={initialMessage}
          />
        ) : (
          <NewAnnouncementComposer
            orgId={orgId}
            onClose={onClose}
            activeEmployeeGroupId={getSelectedEmployeeGroup()}
            hideThread={hideThread}
            initialMessage={initialMessage}
            defaultToAllEmployees={defaultToAllEmployees}
            onMessageSent={onAnnouncementSent}
          />
        )}
      </div>
    </div>
  );
};

export const NewMessageComposer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(stateCityOptionalStepProvider((o) => o.phoneNumber, NewMessageComposerComponent));
